import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectUserAppSettings } from "../../modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { selectActiveApp } from "@/app/globalSlices/appStripSlice";
import { SidebarProvider } from "@/components/ui/sidebar";
import WideSidebar from "../WideSidebar/WideSidebar";
import TopNavBar from "../TopNavBar/TopNavBar";
import MainContent from "../MainContent/MainContent";
import AppLegacyBanner from "@/components/AppLegacyBanner/AppLegacyBanner";
import AnnouncementBanner from "@/components/AnnouncementBanner/AnnouncementBanner";
import SearchModal from "@/modules/hub/features/Search/components/SearchModal/SearchModal";
import { openSearchModal } from "@/modules/hub/features/Search/slices/searchSlice";
import { AppStrip, appStripItems } from "@/components/AppStrip";
import { useActiveAppFromRoute } from "@/hooks/useActiveAppFromRoute";

interface MainLayoutProps {
  section: string | null;
}

const MainLayout: React.FC<MainLayoutProps> = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [sidebarOpenWide, setSidebarOpenWide] = useState(true);
  const [hasManuallyToggled, setHasManuallyToggled] = useState(false);
  const userAppSettings = useSelector(selectUserAppSettings);
  const activeApp = useSelector(selectActiveApp);
  const isAccountSetupIncomplete = userAppSettings && !userAppSettings.roleName;

  // Keep AppStrip active state in sync with the current route
  useActiveAppFromRoute();

  // Check if current app is standalone (no sidebar content)
  const isStandaloneApp =
    activeApp &&
    appStripItems.find((item) => item.id === activeApp)?.standalone;

  // Auto-collapse sidebar on /hub page for landing page experience
  useEffect(() => {
    // Only auto-collapse if user hasn't manually toggled the sidebar
    if (!hasManuallyToggled) {
      if (location.pathname === "/hub") {
        setSidebarOpenWide(false);
      } else {
        setSidebarOpenWide(true);
      }
    }
  }, [location.pathname, hasManuallyToggled]);

  // Auto-collapse sidebar for standalone apps (no sidebar content)
  useEffect(() => {
    if (isStandaloneApp) {
      setSidebarOpenWide(false);
    }
  }, [isStandaloneApp]);

  // Global keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        dispatch(openSearchModal());
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dispatch]);

  const handleSidebarToggle = () => {
    setSidebarOpenWide((prev) => !prev);
    setHasManuallyToggled(true);
  };

  const handleAppStripClick = (appId: string) => {
    const clickedApp = appStripItems.find((item) => item.id === appId);
    // Only expand sidebar for apps that have sidebar content
    if (!clickedApp?.standalone) {
      setSidebarOpenWide(true);
      setHasManuallyToggled(true);
    }
  };

  return (
    <>
      <div className="relative flex h-screen w-full overflow-hidden bg-background text-foreground">
        {/* Main content area with sidebar */}
        <SidebarProvider
          onOpenChange={setSidebarOpenWide}
          open={sidebarOpenWide}
        >
          {/* App Strip - always visible, z-[60] to stay above Sheet overlay, pointer-events-auto to remain clickable when modal Sheet sets pointer-events:none on body */}
          <div className="flex z-[60] pointer-events-auto">
            <AppStrip onAppClick={handleAppStripClick} />
          </div>

          <WideSidebar />
          <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
            {/* <AnnouncementBanner /> */}
            <TopNavBar onSidebarToggle={handleSidebarToggle} />
            <MainContent>
              {isAccountSetupIncomplete ? (
                <div> IncompleteAccountSetup goes here</div>
              ) : (
                <Outlet />
              )}
            </MainContent>
          </div>
        </SidebarProvider>
      </div>
      {/* Global Search Modal */}
      <SearchModal />
    </>
  );
};

export default MainLayout;
